/** Merge lunar, sabbat, eclipse and retrograde data into one filtered, sorted calendar. */
import type { LunarEvent, PrincipalPhase } from "./moon";
import { phaseLabel } from "./moon";
import type { Hemisphere, SabbatDateStyle, Settings } from "./preferences";

export interface SabbatDate {
  sabbat_slug: string;
  hemisphere: Hemisphere;
  year: number;
  astronomical_utc: string;
  solar_longitude: number;
  traditional_date?: string;
}

export interface Eclipse {
  type: "solar" | "lunar";
  kind: string;
  peak_utc: string;
  moon_sign?: string;
  sun_sign?: string;
}

export interface Retrograde {
  planet: string;
  station_retrograde_utc: string;
  station_direct_utc: string;
  sign_at_start: string;
}

export type CalendarKind = "lunar" | "sabbat" | "eclipse" | "retrograde";

export interface CalendarEvent {
  id: string;
  kind: CalendarKind;
  title: string;
  /** ISO timestamp, or YYYY-MM-DD for all-day traditional dates. */
  start: string;
  end?: string;
  /** Content slug to link to (sabbat or moon phase). */
  slug?: string;
  detail?: string;
}

export const SABBAT_NAMES: Record<string, string> = {
  samhain: "Samhain",
  yule: "Yule",
  imbolc: "Imbolc",
  ostara: "Ostara",
  beltane: "Beltane",
  litha: "Litha",
  lughnasadh: "Lughnasadh",
  mabon: "Mabon",
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function lunarTitle(e: LunarEvent): string {
  const base = `${phaseLabel(e.type as PrincipalPhase)} in ${cap(e.moon_sign)}`;
  return e.traditional_name ? `${base} (${e.traditional_name})` : base;
}

/** Date used for a sabbat given the user's preference (traditional falls back to astronomical). */
export function sabbatStart(s: SabbatDate, style: SabbatDateStyle): string {
  return style === "traditional" && s.traditional_date ? s.traditional_date : s.astronomical_utc;
}

/** Comparable millisecond value for either an ISO timestamp or a YYYY-MM-DD date. */
export function startMs(start: string): number {
  return Date.parse(start.length === 10 ? `${start}T00:00:00Z` : start);
}

export interface CalendarSources {
  lunar: LunarEvent[];
  sabbats: SabbatDate[];
  eclipses: Eclipse[];
  retrogrades: Retrograde[];
}

/** Build the user's calendar between `from` and `to` (inclusive), honouring feature toggles. */
export function buildCalendar(
  sources: CalendarSources,
  settings: Pick<Settings, "features" | "hemisphere" | "sabbatDateStyle">,
  from: Date,
  to: Date,
): CalendarEvent[] {
  const lo = from.getTime();
  const hi = to.getTime();
  const out: CalendarEvent[] = [];
  const f = settings.features;

  if (f.lunar) {
    for (const e of sources.lunar) {
      out.push({
        id: `lunar:${e.at_utc}`,
        kind: "lunar",
        title: lunarTitle(e),
        start: e.at_utc,
        slug: e.type,
        detail: e.is_blue_moon ? "Blue moon" : e.is_supermoon ? "Supermoon" : undefined,
      });
    }
  }
  if (f.sabbats) {
    for (const s of sources.sabbats) {
      if (s.hemisphere !== settings.hemisphere) continue;
      out.push({
        id: `sabbat:${s.sabbat_slug}:${s.year}`,
        kind: "sabbat",
        title: SABBAT_NAMES[s.sabbat_slug] ?? cap(s.sabbat_slug),
        start: sabbatStart(s, settings.sabbatDateStyle),
        slug: s.sabbat_slug,
      });
    }
  }
  if (f.eclipses) {
    for (const e of sources.eclipses) {
      const sign = e.type === "lunar" ? e.moon_sign : e.sun_sign;
      out.push({
        id: `eclipse:${e.peak_utc}`,
        kind: "eclipse",
        title: `${cap(e.kind)} ${e.type} eclipse${sign ? ` in ${cap(sign)}` : ""}`,
        start: e.peak_utc,
      });
    }
  }
  if (f.retrogrades) {
    for (const r of sources.retrogrades) {
      out.push({
        id: `retro:${r.planet}:${r.station_retrograde_utc}`,
        kind: "retrograde",
        title: `${cap(r.planet)} retrograde in ${cap(r.sign_at_start)}`,
        start: r.station_retrograde_utc,
        end: r.station_direct_utc,
      });
    }
  }

  return out
    .filter((e) => {
      const s = startMs(e.start);
      const end = e.end ? startMs(e.end) : s;
      return end >= lo && s <= hi;
    })
    .sort((a, b) => startMs(a.start) - startMs(b.start));
}

/** The next sabbat on or after `date` for the user's hemisphere and date style. */
export function nextSabbat(
  sabbats: SabbatDate[],
  settings: Pick<Settings, "hemisphere" | "sabbatDateStyle">,
  date: Date,
): { sabbat: SabbatDate; start: string; daysAway: number } | undefined {
  // Count in the witch's local calendar day (dates are compared as UTC midnights of local Y/M/D).
  const dayStart = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const candidates = sabbats
    .filter((s) => s.hemisphere === settings.hemisphere)
    .map((s) => ({ sabbat: s, start: sabbatStart(s, settings.sabbatDateStyle) }))
    .filter((c) => startMs(c.start) >= dayStart)
    .sort((a, b) => startMs(a.start) - startMs(b.start));
  const first = candidates[0];
  if (!first) return undefined;
  return { ...first, daysAway: Math.floor((startMs(first.start) - dayStart) / 86_400_000) };
}
